/** Vida reutilizável (player, inimigos e futuros bosses usam o mesmo componente). */
export class HealthComponent {
  current: number;
  /** Guarda opcional contra morte (ex.: Void Tree "Unbreakable"): retorna true p/ sobreviver com 1 HP. */
  private lethalGuard?: () => boolean;

  constructor(
    public max: number,
    private readonly onChange?: () => void,
    private readonly onDeath?: () => void,
  ) {
    this.current = max;
  }

  setLethalGuard(fn: (() => boolean) | undefined): void {
    this.lethalGuard = fn;
  }

  get isDead(): boolean {
    return this.current <= 0;
  }

  get fraction(): number {
    return this.max > 0 ? this.current / this.max : 0;
  }

  /** Aplica dano; retorna quanto foi efetivamente aplicado. */
  damage(amount: number): number {
    if (this.isDead || amount <= 0) return 0;
    const before = this.current;
    this.current = Math.max(0, this.current - amount);
    // Unbreakable: intercepta o golpe fatal e sobrevive com 1 HP (se a guarda permitir)
    if (this.current <= 0 && this.lethalGuard && this.lethalGuard()) this.current = 1;
    this.onChange?.();
    if (this.isDead) this.onDeath?.();
    return before - this.current;
  }

  heal(amount: number): void {
    if (this.isDead || amount <= 0) return;
    this.current = Math.min(this.max, this.current + amount);
    this.onChange?.();
  }

  /** Ajusta o máximo (level up). `keepRatio` mantém a proporção de vida atual. */
  setMax(newMax: number, mode: "full" | "keepRatio" | "keepCurrent" = "keepCurrent"): void {
    const ratio = this.fraction;
    this.max = newMax;
    if (mode === "full") this.current = newMax;
    else if (mode === "keepRatio") this.current = Math.round(newMax * ratio);
    else this.current = Math.min(this.current, newMax);
    this.onChange?.();
  }

  /** Aumenta o máximo e a vida atual pelo mesmo valor (level up). */
  increaseMax(amount: number): void {
    this.max += amount;
    this.current += amount;
    this.onChange?.();
  }

  reset(): void {
    this.current = this.max;
    this.onChange?.();
  }
}
